import * as React from 'react'
import * as Enum from '@owl-nest/legacy-enum'

export const Step = Enum.create({
  BEFORE: 'before',
  DURING: 'during',
  AFTER: 'after',
})
export type Step = Enum.ValueOf<typeof Step>

export type UseTransition = {
  step: Step
  unmounted: boolean
  onTransitionEnd: (event: React.TransitionEvent) => void
}

const TRANSITION_END_FALLBACK_MS = 500

export function useTransition(visible: boolean): UseTransition {
  const [shouldRender, setRender] = React.useState(false)

  React.useEffect(() => {
    if (!visible) {
      return
    }

    // Keep the initial "before" render separate from the following "during" render.
    const timeout = window.setTimeout(() => {
      setRender(true)
    }, 0)

    return () => window.clearTimeout(timeout)
  }, [visible])

  React.useEffect(() => {
    if (visible || !shouldRender) {
      return
    }

    // Browsers do not guarantee transitionend (for example when a transition is
    // interrupted or disabled), so never leave hidden UI mounted indefinitely.
    const timeout = window.setTimeout(() => setRender(false), TRANSITION_END_FALLBACK_MS)

    return () => window.clearTimeout(timeout)
  }, [shouldRender, visible])

  const onTransitionEnd = (event: React.TransitionEvent): void => {
    // since transitionend bubble, check if the event came from our element. If
    // it came from a different element, we do nothing
    if (event.target !== event.currentTarget) {
      return
    }
    if (!visible) {
      setRender(false)
    }
  }

  let step: Step
  if (visible && !shouldRender) {
    step = Step.BEFORE
  } else if (!visible && shouldRender) {
    step = Step.AFTER
  } else {
    step = Step.DURING
  }

  const unmounted = !visible && !shouldRender

  return { step, unmounted, onTransitionEnd }
}
